import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Check, FileText, LayoutGrid, List, Plus, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Segmented } from "@/components/layout/segmented";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { EmptyState } from "@/components/data/empty-state";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { ChipEstadoCarga, StatusChip } from "@/components/mercado/status-chip";
import { SelectDestino } from "@/components/mercado/select-destino";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { origemAtual, useNavegacao } from "@/lib/navegacao";
import { useToast } from "@/hooks/use-toast";
import { useCriarCarga, useDestinos, useQuadroDeCargas } from "@/hooks/use-cargas";
import { useIncluirEmCarga, useRomaneios } from "@/hooks/use-romaneios";
import { fmtKgBR, fmtDiaCurto, fmtToneladas, fmtDataCurtaBR } from "@/lib/formato-mercado";
import { CAPACIDADE_REFERENCIA_KG } from "@shared/mercado-inicio";
import { PERIODOS_ROMANEIOS, rotuloDoPeriodo, type PeriodoRomaneios } from "@shared/mercado-romaneios";
import {
  CARGA_STATUS_LABEL, type CargaLinhaDTO, type CargaStatus, type QuadroCargasDTO,
} from "@shared/mercado-comprador";
import { cn } from "@/lib/utils";

/**
 * CARGAS — os caminhões que o corretor monta.
 *
 * ⚠️ SÓ O CONTROLE DELE, SEM VENDA (decisão D2). Preço ao cliente, margem, a
 * receber e vencimento de venda ficam fora desta fase: as colunas continuam no
 * banco, e o que a tela mostra é o que ele acompanha hoje — o que está montando,
 * o que já saiu (e se o cliente recebeu o documento) e o que foi entregue.
 *
 * ⚠️ QUADRO POR SITUAÇÃO, não lista filtrada: "o que está andando" é a pergunta
 * de todo dia, e respondê-la com um filtro obrigaria a trocar de recorte três
 * vezes para ver o mesmo. No celular vira uma coluna só, na mesma ordem.
 *
 * ⚠️ NENHUMA CONTA AQUI. KPIs, faltam e kg chegam prontos do servidor; a única
 * aritmética permitida é de LAYOUT (largura de barra), que é geometria.
 */

// ⚠️ Constantes de MÓDULO (são deps de `useMemo` dentro do hook).
const PADRAO = { visao: "quadro", periodo: "mes", de: "", ate: "", cliente: "todos", q: "" };
const PERMITIDOS = { visao: ["quadro", "lista"], periodo: PERIODOS_ROMANEIOS } as const;

/** Capacidades de caminhão que existem de verdade na praça. */
const CAPACIDADES_KG = [16_000, 18_000, 20_000, 24_000];

const ORDEM_COLUNAS: CargaStatus[] = ["EM_FORMACAO", "FECHADA", "ENTREGUE"];

export default function MercadoCargas() {
  const { user } = useAuth();
  const ehCorretor = user?.orgTipo === "COMPRADOR";
  const podeOperar = usePode("mercado.corretor.operar");
  const { estado, definir } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const [novaAberta, setNovaAberta] = useState(false);

  const filtro = useMemo(() => ({
    periodo: estado.periodo,
    de: estado.de || undefined,
    ate: estado.ate || undefined,
    cliente: estado.cliente,
    q: estado.q || undefined,
  }), [estado]);

  const consulta = useQuadroDeCargas(filtro, ehCorretor);
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "cards" });
  const { data: clientes } = useDestinos(ehCorretor);

  const total = data
    ? ORDEM_COLUNAS.reduce((a, s) => a + data.colunas[s].length, 0)
    : 0;

  return (
    <CompradorShell className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight" data-testid="titulo-cargas">Cargas</h1>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-[60ch]">
            Os caminhões que você monta com os romaneios e envia aos seus clientes.
          </p>
        </div>
        <div className="flex gap-2">
          <Segmented<"quadro" | "lista">
            valor={estado.visao as "quadro" | "lista"}
            onChange={v => definir({ visao: v })}
            testId="seg-visao-cargas"
            opcoes={[
              { id: "quadro", label: "Quadro", icone: LayoutGrid },
              { id: "lista", label: "Lista", icone: List },
            ]}
          />
          {podeOperar && (
            <Button onClick={() => setNovaAberta(true)} data-testid="button-nova-carga">
              <Plus className="h-4 w-4 mr-1.5" />Nova carga
            </Button>
          )}
        </div>
      </header>

      {aguardando ?? (data && (
        <>
          <Tiles dados={data} periodo={estado.periodo as PeriodoRomaneios} />

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Segmented<PeriodoRomaneios>
              valor={estado.periodo as PeriodoRomaneios}
              onChange={v => definir(v === "personalizado" ? { periodo: v } : { periodo: v, de: "", ate: "" })}
              testId="seg-periodo-cargas"
              className="[&>button]:flex-1 sm:[&>button]:flex-none"
              opcoes={[
                { id: "mes", label: "Este mês" },
                { id: "30d", label: "30 dias" },
                { id: "tudo", label: "Tudo" },
                { id: "personalizado", label: "Período…" },
              ]}
            />
            <Select value={estado.cliente} onValueChange={v => definir({ cliente: v })}>
              <SelectTrigger className="sm:w-52" data-testid="filtro-cliente">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os clientes</SelectItem>
                {(clientes ?? []).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={estado.q}
              onChange={e => definir({ q: e.target.value })}
              placeholder="Buscar CG, cliente ou produtor"
              className="sm:ml-auto sm:w-60"
              data-testid="busca-cargas"
              aria-label="Buscar carga, cliente ou produtor"
            />
          </div>

          {estado.periodo === "personalizado" && (
            <div className="flex gap-2">
              <Input type="date" value={estado.de} onChange={e => definir({ de: e.target.value })} aria-label="De" />
              <Input type="date" value={estado.ate} onChange={e => definir({ ate: e.target.value })} aria-label="Até" />
            </div>
          )}

          {total === 0 ? (
            <EmptyState
              icone={Truck}
              titulo="Nenhuma carga neste recorte"
              descricao="Uma carga junta o que você comprou de vários produtores num caminhão só — com romaneio do Rizoma ou com o seu registro."
              acao={podeOperar
                ? <Button onClick={() => setNovaAberta(true)}><Plus className="h-4 w-4 mr-1.5" />Nova carga</Button>
                : undefined}
            />
          ) : estado.visao === "quadro" ? (
            <Quadro dados={data} periodo={estado.periodo as PeriodoRomaneios} podeOperar={podeOperar} onNova={() => setNovaAberta(true)} />
          ) : (
            <Lista dados={data} />
          )}
        </>
      ))}

      <DialogNovaCarga aberta={novaAberta} onFechar={() => setNovaAberta(false)} />
    </CompradorShell>
  );
}

// ─── Os quatro números ───────────────────────────────────────────────────────

function Tile({ rotulo, alerta, children }: { rotulo: string; alerta?: boolean; children: React.ReactNode }) {
  return (
    <section className={cn(
      "rounded-lg border bg-card p-4 flex flex-col gap-1.5 min-h-[128px]",
      alerta && "border-[hsl(var(--warning,38_92%_50%))]",
    )}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{rotulo}</p>
      {children}
    </section>
  );
}

function Numerao({ valor, unidade, testId }: { valor: string; unidade?: string; testId?: string }) {
  return (
    <p className="font-mono text-[26px] font-semibold tabular-nums leading-tight" data-testid={testId}>
      {valor}
      {unidade && <span className="ml-1.5 text-[13px] font-medium text-muted-foreground font-sans">{unidade}</span>}
    </p>
  );
}

function Tiles({ dados, periodo }: { dados: QuadroCargasDTO; periodo: PeriodoRomaneios }) {
  const { emFormacao, fechadas, entreguesNoPeriodo, romaneiosSemCarga } = dados.kpis;
  const hoje = new Date().toISOString().slice(0, 10);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tiles-cargas">
      <Tile rotulo="Em formação">
        <Numerao valor={String(emFormacao.n)} unidade={emFormacao.n === 1 ? "carga" : "cargas"} testId="tile-em-formacao" />
        <p className="text-xs text-muted-foreground">
          {emFormacao.faltamPorCarga.length > 0
            ? emFormacao.faltamPorCarga
              .map(c => c.faltam != null && c.faltam > 0
                ? `${c.identificador}: faltam ${fmtKgBR(c.faltam)}`
                : `${c.identificador}: completa`)
              .join(" · ")
            : "nenhuma carga em montagem"}
        </p>
      </Tile>

      <Tile rotulo="Fechadas · em trânsito">
        <Numerao valor={String(fechadas.n)} unidade={fechadas.n === 1 ? "carga" : "cargas"} testId="tile-fechadas" />
        <p className="text-xs text-muted-foreground">
          {/* É a pendência que mais escapa: a carga saiu e o cliente não recebeu
              o documento. Por isso o número vive no topo, não num chip da coluna. */}
          {fechadas.n > 0 ? <>{fechadas.comDocumentoEnviado} com documento enviado</> : "nenhuma em trânsito"}
        </p>
      </Tile>

      <Tile rotulo={`Entregues · ${rotuloDoPeriodo(periodo, hoje)}`}>
        <Numerao valor={String(entreguesNoPeriodo.n)} unidade="cargas" testId="tile-entregues" />
        <p className="text-xs text-muted-foreground">
          {fmtToneladas(entreguesNoPeriodo.kg)} · {entreguesNoPeriodo.nRomaneios} {entreguesNoPeriodo.nRomaneios === 1 ? "romaneio" : "romaneios"}
        </p>
      </Tile>

      <Tile rotulo="Romaneios sem carga" alerta={romaneiosSemCarga.n > 0}>
        <Numerao
          valor={String(romaneiosSemCarga.n)}
          unidade={romaneiosSemCarga.kg > 0 ? `· ${fmtToneladas(romaneiosSemCarga.kg)}` : undefined}
          testId="tile-sem-carga"
        />
        <p className="text-xs text-muted-foreground truncate">
          {romaneiosSemCarga.produtores.length > 0
            ? romaneiosSemCarga.produtores.join(", ")
            : "tudo já está em algum caminhão"}
        </p>
        {romaneiosSemCarga.n > 0 && (
          <Link
            href="/mercado/romaneios?situacao=SEM_CARGA"
            state={origemAtual()}
            className="mt-auto text-xs font-semibold text-primary"
            data-testid="link-selecionar-e-montar"
          >
            Selecionar e montar →
          </Link>
        )}
      </Tile>
    </div>
  );
}

// ─── Quadro ──────────────────────────────────────────────────────────────────

function Quadro({
  dados, periodo, podeOperar, onNova,
}: { dados: QuadroCargasDTO; periodo: PeriodoRomaneios; podeOperar: boolean; onNova: () => void }) {
  const hoje = new Date().toISOString().slice(0, 10);
  return (
    // Três colunas no desktop; UMA no celular, na mesma ordem — o quadro não
    // vira carrossel horizontal, que esconderia justamente o que está andando.
    <div className="grid gap-4 lg:grid-cols-3 lg:items-start" data-testid="quadro-cargas">
      {ORDEM_COLUNAS.map(status => (
        <section key={status} className="space-y-2 min-w-0">
          <header className="flex items-baseline gap-2 px-0.5">
            <h2 className="text-sm font-semibold">{CARGA_STATUS_LABEL[status]}</h2>
            <span className="text-xs text-muted-foreground">
              {status === "ENTREGUE" && <>{rotuloDoPeriodo(periodo, hoje)} · </>}
              {dados.colunas[status].length}
            </span>
          </header>

          {dados.colunas[status].length === 0 ? (
            <p className="rounded-lg border border-dashed p-4 text-xs text-muted-foreground text-center">
              Nenhuma carga {CARGA_STATUS_LABEL[status].toLowerCase()}
            </p>
          ) : (
            <ul className="space-y-2">
              {dados.colunas[status].map(c => <li key={c.id}><CardCarga carga={c} /></li>)}
            </ul>
          )}

          {status === "EM_FORMACAO" && podeOperar && (
            <Button variant="ghost" size="sm" onClick={onNova} data-testid="button-nova-carga-coluna">
              <Plus className="h-4 w-4 mr-1" />Nova carga
            </Button>
          )}
        </section>
      ))}
    </div>
  );
}

function CardCarga({ carga }: { carga: CargaLinhaDTO }) {
  const { resumo } = carga;
  const emFormacao = carga.status === "EM_FORMACAO";
  const pct = resumo.pctDoAlvo != null ? Math.min(100, Math.round(resumo.pctDoAlvo * 100)) : null;

  return (
    <Link
      href={`/mercado/cargas/${carga.id}`}
      state={origemAtual()}
      className="block rounded-lg border bg-card p-3.5 space-y-2 hover-elevate"
      data-testid={`card-carga-${carga.identificador}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono font-semibold">{carga.identificador}</p>
          <p className="text-[13px] text-muted-foreground truncate">
            {carga.clienteNome
              ? `${carga.clienteNome}${carga.clienteLocal ? ` · ${carga.clienteLocal}` : ""}`
              : "Cliente a definir"}
          </p>
        </div>
        <ChipEstadoCarga status={carga.status} />
      </div>

      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span className="font-mono text-xl font-semibold tabular-nums">
          {fmtKgBR(resumo.kgLiquido)}
          <span className="ml-1 text-xs font-medium text-muted-foreground font-sans">
            {emFormacao && carga.alvoKg ? `de ${fmtKgBR(carga.alvoKg)}` : "kg"}
          </span>
        </span>
        {emFormacao && resumo.faltam != null && resumo.faltam > 0 && (
          <span className="text-xs font-semibold text-[hsl(var(--warning,38_92%_40%))]">
            faltam {fmtKgBR(resumo.faltam)}
          </span>
        )}
      </div>

      {/* A barra só existe EM FORMAÇÃO: depois de fechada, o que importa é o
          líquido real, e uma barra de progresso ali sugeriria que ainda cabe
          alguma coisa no caminhão. */}
      {emFormacao && pct != null && (
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" aria-hidden>
          <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex -space-x-1.5">
          {carga.produtores.slice(0, 4).map((p, i) => (
            <span
              key={i}
              className={cn(
                "h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold ring-1 ring-card",
                p.noRizoma ? "bg-[hsl(var(--chart-1))] text-white" : "bg-muted",
              )}
              title={p.noRizoma ? "produtor do Rizoma" : "registro próprio"}
            >
              {p.iniciais}
            </span>
          ))}
        </span>
        <span>
          {resumo.nProdutores} {resumo.nProdutores === 1 ? "produtor" : "produtores"}
          {" · "}{resumo.nRomaneios} {resumo.nRomaneios === 1 ? "romaneio" : "romaneios"}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-muted-foreground">
        <span>
          {carga.status === "ENTREGUE" && carga.entregueEm
            ? `entregue ${fmtDataCurtaBR(carga.entregueEm.slice(0, 10))}`
            : carga.dataCarregamento
              ? `${emFormacao ? "carregamento" : "carregou"} ${fmtDiaCurto(carga.dataCarregamento)}`
              : "sem data de carregamento"}
        </span>
        {/* Só em carga que já saiu: em formação o documento nem existe ainda. */}
        {!emFormacao && (
          carga.documentoEnviadoEm
            ? <StatusChip kind="conf"><Check className="h-3 w-3 mr-0.5" />Documento enviado {fmtDataCurtaBR(carga.documentoEnviadoEm.slice(0, 10))}</StatusChip>
            : <StatusChip kind="warn">Documento não enviado</StatusChip>
        )}
      </div>
    </Link>
  );
}

// ─── Lista ───────────────────────────────────────────────────────────────────

function Lista({ dados }: { dados: QuadroCargasDTO }) {
  const { abrir } = useNavegacao();
  const linhas = ORDEM_COLUNAS.flatMap(s => dados.colunas[s]);

  return (
    <>
      {/* No celular a lista É o quadro numa coluna — repetir a tabela em cartões
          seria a terceira aparência dos mesmos dados. */}
      <ul className="md:hidden space-y-2">
        {linhas.map(c => <li key={c.id}><CardCarga carga={c} /></li>)}
      </ul>

      <div className="hidden md:block rounded-lg border overflow-hidden">
        <table className="w-full text-sm" data-testid="tabela-cargas">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left font-medium p-2">Carga</th>
              <th className="text-left font-medium p-2">Cliente</th>
              <th className="text-left font-medium p-2">Carregamento</th>
              <th className="text-right font-medium p-2">kg</th>
              <th className="text-left font-medium p-2">Produtores</th>
              <th className="text-left font-medium p-2">Situação</th>
              <th className="text-left font-medium p-2">Documento</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(c => (
              <tr
                key={c.id}
                onClick={() => abrir(`/mercado/cargas/${c.id}`)}
                className="border-t cursor-pointer hover-elevate"
                data-testid={`linha-carga-${c.identificador}`}
              >
                <td className="p-2">
                  <b className="font-mono">{c.identificador}</b>
                  <span className="block text-[11px] text-muted-foreground">
                    {c.variedade ? cap(c.variedade) : "Mista"}
                  </span>
                </td>
                <td className="p-2">
                  <b className="block truncate max-w-[180px]">{c.clienteNome ?? "A definir"}</b>
                  <span className="block text-[11px] text-muted-foreground truncate max-w-[180px]">{c.clienteLocal ?? "—"}</span>
                </td>
                <td className="p-2 text-xs">{c.dataCarregamento ? fmtDiaCurto(c.dataCarregamento) : "—"}</td>
                <td className="p-2 text-right font-mono tabular-nums">{fmtKgBR(c.resumo.kgLiquido)}</td>
                <td className="p-2 text-xs">
                  {c.resumo.nProdutores} · {c.resumo.nRomaneios} {c.resumo.nRomaneios === 1 ? "romaneio" : "romaneios"}
                </td>
                <td className="p-2"><ChipEstadoCarga status={c.status} /></td>
                <td className="p-2">
                  {c.status === "EM_FORMACAO"
                    ? <span className="text-muted-foreground">—</span>
                    : c.documentoEnviadoEm
                      ? <StatusChip kind="conf">Enviado {fmtDataCurtaBR(c.documentoEnviadoEm.slice(0, 10))}</StatusChip>
                      : <StatusChip kind="warn">Não enviado</StatusChip>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Nova carga ──────────────────────────────────────────────────────────────

/**
 * Nova carga: um caminhão, um cliente, uma data.
 *
 * ⚠️ SEM PREÇO DE VENDA (D2) — e o servidor ignora o campo se ele vier. Nesta
 * fase a carga nasce sem dados de venda.
 *
 * Os romaneios sem carga entram JÁ MARCADOS: é o caminho mais curto do papel ao
 * caminhão, e criar a carga vazia para depois procurar cada romaneio numa
 * segunda lista é o que fazia nascer a carga abandonada que o Início filtra.
 */
function DialogNovaCarga({ aberta, onFechar }: { aberta: boolean; onFechar: () => void }) {
  const { toast } = useToast();
  const { substituir } = useNavegacao();
  const criar = useCriarCarga();
  const incluir = useIncluirEmCarga();

  const [variedade, setVariedade] = useState("MISTA");
  const [data, setData] = useState("");
  const [alvo, setAlvo] = useState(String(CAPACIDADE_REFERENCIA_KG));
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [placa, setPlaca] = useState("");
  const [motorista, setMotorista] = useState("");
  const [marcados, setMarcados] = useState<Set<string>>(new Set());

  // Só busca com o diálogo aberto: a lista de romaneios sem carga é uma consulta
  // inteira, e disparar no mount de Cargas custaria em toda abertura da tela.
  const semCarga = useRomaneios({ periodo: "tudo", situacao: "SEM_CARGA" }, aberta);
  const disponiveis = semCarga.data?.linhas ?? [];
  const kgMarcado = disponiveis.filter(r => marcados.has(r.ref)).reduce((a, r) => a + r.kgBase, 0);
  const alvoKg = alvo ? parseInt(alvo, 10) : null;
  const acimaDaCapacidade = alvoKg != null && kgMarcado > alvoKg;

  function salvar() {
    criar.mutate(
      {
        // NULL = carga MISTA, não "não informado": Prata e Nanica no mesmo
        // caminhão é rotina.
        variedade: variedade === "MISTA" ? null : variedade,
        dataCarregamento: data || null,
        alvoKg,
        destinoContatoId: clienteId,
        placa: placa.trim() || null,
        motoristaNome: motorista.trim() || null,
      },
      {
        onSuccess: async (carga) => {
          const refs = Array.from(marcados);
          if (refs.length > 0) {
            try {
              await incluir.mutateAsync({ refs, cargaId: carga.id });
            } catch (e) {
              // A CARGA JÁ FOI CRIADA — dizer isso importa, senão ele tenta de
              // novo e nasce a segunda carga vazia.
              toast({
                title: `${carga.identificador} criada, mas não consegui incluir os romaneios`,
                description: (e as Error).message,
                variant: "destructive",
              });
            }
          }
          onFechar();
          // `substituir`: a carga recém-criada troca de URL sem deixar o diálogo
          // no histórico — "Voltar" leva à lista, não a um estado morto.
          substituir(`/mercado/cargas/${carga.id}`);
        },
        // Nunca engolir: sem isto ele toca em Criar e nada acontece.
        onError: (e: Error) => toast({
          title: "Não consegui criar a carga",
          description: e.message,
          variant: "destructive",
        }),
      },
    );
  }

  return (
    <Dialog open={aberta} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova carga</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Um caminhão, um cliente, uma data. Os valores de venda ficam fora por enquanto.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <SelectDestino valor={clienteId} onChange={setClienteId} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="carga-data">Carregamento</Label>
              <Input id="carga-data" type="date" value={data} onChange={e => setData(e.target.value)} data-testid="input-data-carregamento" />
            </div>
            <div className="space-y-2">
              <Label>Variedade</Label>
              <Segmented<string>
                valor={variedade}
                onChange={setVariedade}
                testId="seg-variedade"
                className="[&>button]:flex-1"
                opcoes={[
                  { id: "PRATA", label: "Prata" },
                  { id: "NANICA", label: "Nanica" },
                  { id: "MISTA", label: "Mista" },
                ]}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Capacidade do caminhão</Label>
            <div className="flex gap-2 flex-wrap">
              {CAPACIDADES_KG.map(kg => (
                <Button
                  key={kg} type="button" size="sm"
                  variant={alvo === String(kg) ? "default" : "outline"}
                  onClick={() => setAlvo(String(kg))}
                  data-testid={`chip-capacidade-${kg}`}
                >
                  {kg / 1000} t
                </Button>
              ))}
              <Input
                type="number" inputMode="numeric" placeholder="kg"
                className="w-24" value={alvo} onChange={e => setAlvo(e.target.value)}
                aria-label="Outra capacidade em kg"
              />
            </div>
            <p className="text-xs text-muted-foreground">Serve para mostrar quanto falta. Pode mudar depois.</p>
          </div>

          <div className="space-y-2">
            <Label>
              Romaneios que já entram{" "}
              <span className="text-muted-foreground font-normal">
                {disponiveis.length} sem carga
              </span>
            </Label>
            {semCarga.isLoading ? (
              <p className="text-xs text-muted-foreground">Carregando os romaneios sem carga…</p>
            ) : disponiveis.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Todos os romaneios já estão em algum caminhão. A carga nasce vazia e você inclui depois.
              </p>
            ) : (
              <>
                <ul className="rounded-lg border divide-y max-h-52 overflow-y-auto" data-testid="lista-romaneios-nova-carga">
                  {disponiveis.map(r => (
                    <li key={r.ref}>
                      <label className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover-elevate">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={marcados.has(r.ref)}
                          onChange={() => setMarcados(atual => {
                            const proximo = new Set(atual);
                            if (proximo.has(r.ref)) proximo.delete(r.ref);
                            else proximo.add(r.ref);
                            return proximo;
                          })}
                          data-testid={`check-nova-carga-${r.ref}`}
                        />
                        <span className="min-w-0 flex-1">
                          <b>{r.produtor.nome}</b>
                          <span className="text-xs text-muted-foreground">
                            {" · "}{r.origem === "RIZOMA" ? r.numero : `bloco ${r.numero}`}
                            {" · "}{fmtDiaCurto(r.data)}
                          </span>
                          {r.conferencia === "A_CONFERIR" && (
                            <span className="block mt-0.5"><StatusChip kind="warn">A conferir</StatusChip></span>
                          )}
                        </span>
                        <span className="font-mono text-sm font-semibold tabular-nums shrink-0">{fmtKgBR(r.kgBase)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Selecionados: <b className="font-mono">{fmtKgBR(kgMarcado)}</b>
                  {alvoKg != null && <> de {fmtKgBR(alvoKg)}</>}
                  {alvoKg != null && !acimaDaCapacidade && <> · faltam <b className="font-mono">{fmtKgBR(alvoKg - kgMarcado)}</b></>}
                  {acimaDaCapacidade && (
                    <> · <b className="text-[hsl(var(--warning,38_92%_40%))]">acima da capacidade</b></>
                  )}
                </p>
              </>
            )}
          </div>

          <details className="rounded-lg border p-3">
            <summary className="cursor-pointer text-sm font-medium">Placa e motorista (opcional)</summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div className="space-y-2">
                <Label htmlFor="carga-placa">Placa</Label>
                <Input id="carga-placa" value={placa} onChange={e => setPlaca(e.target.value)} placeholder="ABC-1D23" className="font-mono" data-testid="input-placa" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="carga-motorista">Motorista</Label>
                <Input id="carga-motorista" value={motorista} onChange={e => setMotorista(e.target.value)} placeholder="Nome" data-testid="input-motorista" />
              </div>
            </div>
          </details>
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          <span className="text-xs text-muted-foreground self-center">
            {/* O número é do SERVIDOR (sequencial por org, com UNIQUE + retry).
                A tela não o adivinha — adivinhar criaria dois CG-0015. */}
            O número é gerado ao criar.
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onFechar}>Cancelar</Button>
            <Button onClick={salvar} disabled={criar.isPending} data-testid="button-criar-carga">
              {criar.isPending ? "Criando…" : "Criar carga"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.substring(1).toLowerCase();
}

/** Fração → "6,5%". O servidor manda fração; o formato é da tela. */
export function fmtPct(fracao: number): string {
  return `${(fracao * 100).toFixed(1).replace(".", ",")}%`;
}
